import React, { useLayoutEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { RootState, useAppDispatch } from "../../redux/store";
import { setTopNavNameTitle } from "../../redux/features/mobileLayout";
import { getNotificationDetail as getMobileNotificationDetail } from "../../redux/features/mobileNotifications";
import { Loader2 } from "lucide-react";
import { DateTimeUtil } from "../../utils";
import { NgwAvatar } from "../../components/common";
import { ApprovalHistory } from "../../components/notificationv2";

const OpenShiftAvailableNotificationDetail: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const notification = useSelector((state: RootState) => state.mobileNotifications.selectedNotification);
    const fetchedDetail = useSelector((state: RootState) => state.mobileNotifications.fetchedNotificationDetails);
    const user = useSelector((state: RootState) => state.user.user);
    const isLoading = useSelector((state: RootState) => state.mobileNotifications.isNotificationDetailLoading);
    const [isRefreshing, setIsRefreshing] = useState(false);

    const title = notification?.message_info?.title || fetchedDetail?.message_info?.title || "Available Open Shift";
    const body = notification?.message_info?.body || fetchedDetail?.message_info?.body || "";
    
    // Extract open shift details from notification
    const openShiftDetails = fetchedDetail?.open_shift_details;
    const releasedRequestor = openShiftDetails?.released_requestor;
    const claimedRequestor = openShiftDetails?.claimed_requestor;

    useLayoutEffect(() => {
        dispatch(setTopNavNameTitle("Open Shifts"));
    }, [dispatch]);

    useLayoutEffect(() => {
        const id = notification?.notification_id;
        if (id && user?.id) {
            dispatch(
                getMobileNotificationDetail({
                    notificationId: id,
                    employeeId: user.id,
                })
            );
        }
    }, [dispatch, notification?.notification_id, user?.id]);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-40 w-full">
                <Loader2 className="w-8 h-8 text-gray-600 animate-spin" />
            </div>
        );
    }

    const CurrentApproversSection = () => {
        return <ApprovalHistory workflowDetails={fetchedDetail?.workflow_details} title="Approval History" />;
    };

    return (
        <div className="bg-gray-50 min-h-screen pb-8">
            <div className="w-full md:w-1/2 mx-auto">
                {/* Employee Info Cards */}
                {(releasedRequestor || claimedRequestor) && (
                    <div className="mx-4 mt-4">
                        {/* Mobile Layout - Vertical Stack */}
                        <div className="block md:hidden space-y-3">
                            {/* Released Employee Card */}
                            {releasedRequestor && (
                                <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-l-blue-500">
                                    <div className="flex items-start space-x-3">
                                        <div className="w-12 h-12 relative">
                                            <NgwAvatar src={releasedRequestor?.image_url} name={releasedRequestor?.emp_name} />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-2 mb-1">
                                                <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                                                    RELEASED
                                                </span>
                                            </div>
                                            <h2 className="text-lg font-semibold text-gray-900 mb-1">
                                                {releasedRequestor?.emp_name}
                                            </h2>
                                            <p className="text-gray-600 text-sm leading-relaxed">
                                                Released this shift
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Transfer Arrow for Mobile */}
                            {releasedRequestor && claimedRequestor && (
                                <div className="flex justify-center text-gray-400">
                                    <div className="flex flex-col items-center">
                                        <svg className="w-6 h-6 rotate-90" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                        </svg>
                                        <span className="text-xs text-gray-500 mt-1">Shift Transfer</span>
                                    </div>
                                </div>
                            )}

                            {/* Claimed Employee Card */}
                            {claimedRequestor && (
                                <div className="bg-white rounded-lg shadow-sm p-4 border-l-4 border-l-green-500">
                                    <div className="flex items-start space-x-3">
                                        {claimedRequestor?.emp_name && (
                                            <div className="w-12 h-12 relative">
                                                <NgwAvatar src={claimedRequestor?.image_url} name={claimedRequestor?.emp_name} />
                                            </div>
                                        )}
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-2 mb-1">
                                                <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded-full">
                                                    CLAIMED
                                                </span>
                                            </div>
                                            <h2 className="text-lg font-semibold text-gray-900 mb-1">
                                                {claimedRequestor?.emp_name}
                                            </h2>
                                            <p className="text-gray-600 text-sm leading-relaxed">
                                                Now owns this shift
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Desktop Layout - Horizontal Flow */}
                        <div className="hidden md:flex items-center justify-between">
                            {/* Released Employee Card - Left side */}
                            {releasedRequestor && (
                                <div className="bg-white rounded-lg shadow-sm p-4 flex-1 mr-2 border-l-4 border-l-blue-500">
                                    <div className="flex items-start space-x-3">
                                        <div className="w-12 h-12 relative">
                                            <NgwAvatar src={releasedRequestor?.image_url} name={releasedRequestor?.emp_name} />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-2 mb-1">
                                                <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-2 py-1 rounded-full">
                                                    RELEASED
                                                </span>
                                            </div>
                                            <h2 className="text-lg font-semibold text-gray-900 mb-1">
                                                {releasedRequestor?.emp_name}
                                            </h2>
                                            <p className="text-gray-600 text-sm leading-relaxed">
                                                Released this shift
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Arrow showing transfer */}
                            {releasedRequestor && claimedRequestor && (
                                <div className="flex flex-col items-center mx-2 text-gray-400">
                                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                                    </svg>
                                    <span className="text-xs text-gray-500 mt-1">Shift Transfer</span>
                                </div>
                            )}

                            {/* Claimed Employee Card - Right side */}
                            {claimedRequestor && (
                                <div className="bg-white rounded-lg shadow-sm p-4 flex-1 ml-2 border-l-4 border-l-green-500">
                                    <div className="flex items-start space-x-3">
                                        <div className="w-12 h-12 relative">
                                            <NgwAvatar src={claimedRequestor?.image_url} name={claimedRequestor?.emp_name} />
                                        </div>
                                        <div className="flex-1">
                                            <div className="flex items-center space-x-2 mb-1">
                                                <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-1 rounded-full">
                                                    CLAIMED
                                                </span>
                                            </div>
                                            <h2 className="text-lg font-semibold text-gray-900 mb-1">
                                                {claimedRequestor?.emp_name}
                                            </h2>
                                            <p className="text-gray-600 text-sm leading-relaxed">
                                                Now owns this shift
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {/* Overall Status Card */}
                {openShiftDetails && (
                    <div className="bg-white mx-4 rounded-lg shadow-sm p-4 mb-3 mt-4">
                        <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                            <div className="mb-3 md:mb-0">
                                <h3 className="text-lg font-semibold text-gray-900">Shift Status</h3>
                                <p className="text-sm text-gray-500 mt-1">
                                    Current state of the open shift
                                </p>
                            </div>
                            <div className="text-left md:text-right">
                                <span className={`px-3 py-1 rounded-full text-sm font-semibold capitalize ${
                                    openShiftDetails?.status === 'draft' ? 'bg-gray-100 text-gray-700' :
                                    openShiftDetails?.status === 'published' ? 'bg-blue-100 text-blue-700' :
                                    openShiftDetails?.status === 'picked' ? 'bg-yellow-100 text-yellow-700' :
                                    openShiftDetails?.status === 'approved' ? 'bg-green-100 text-green-700' :
                                    openShiftDetails?.status === 'rejected' ? 'bg-red-100 text-red-700' :
                                    openShiftDetails?.status === 'cancelled' ? 'bg-gray-100 text-gray-700' :
                                    'bg-gray-100 text-gray-700'
                                }`}>
                                    {openShiftDetails?.status === 'draft' ? 'Draft' :
                                     openShiftDetails?.status === 'published' ? 'Published' :
                                     openShiftDetails?.status === 'picked' ? 'Picked' :
                                     openShiftDetails?.status === 'approved' ? 'Approved' :
                                     openShiftDetails?.status === 'rejected' ? 'Rejected' :
                                     openShiftDetails?.status === 'cancelled' ? 'Cancelled' :
                                     'Unknown'}
                                </span>
                                <p className="text-xs text-gray-500 mt-1">
                                    {openShiftDetails?.status === 'draft' ? 'Shift is being prepared' :
                                     openShiftDetails?.status === 'published' ? 'Shift is available for claiming' :
                                     openShiftDetails?.status === 'picked' ? 'Shift has been claimed' :
                                     openShiftDetails?.status === 'approved' ? 'Shift transfer approved' :
                                     openShiftDetails?.status === 'rejected' ? 'Shift transfer rejected' :
                                     openShiftDetails?.status === 'cancelled' ? 'Shift transfer cancelled' :
                                     'Status unknown'}
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Main Content Card */}
                <div className="bg-white mx-4 rounded-lg shadow-sm p-4 mb-3">
                    <h1 className="text-2xl font-bold text-gray-900 mb-2">{title}</h1>
                    {body && <p className="text-gray-700 mb-6">{body}</p>}

                    {/* Shift Details */}
                    {openShiftDetails && (
                        <div className="space-y-4 mb-6">
                            <div>
                                <h3 className="text-lg font-bold text-gray-900 mb-3 flex items-center">
                                    <svg className="w-5 h-5 mr-2 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                    Shift Schedule
                                </h3>
                                <div className="bg-gray-50 rounded-lg p-3">
                                    <h4 className="text-lg md:text-xl font-bold text-gray-900 mb-1 break-words">
                                        {DateTimeUtil.formatDateTimeRangeUtc(
                                            `${openShiftDetails?.shift_date} ${openShiftDetails?.shift_start_time}`,
                                            `${openShiftDetails?.shift_date} ${openShiftDetails?.shift_end_time}`,
                                            "Invalid Date provided",
                                            true
                                        )}
                                    </h4>
                                    <p className="text-gray-600 text-sm">{openShiftDetails?.shift_name || "Main Office"}</p>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Show approval workflow if available */}
                    {fetchedDetail?.workflow_details && fetchedDetail.workflow_details.length > 0 && (
                        <CurrentApproversSection />
                    )}

                    <button
                        type="button"
                        className="w-full border border-blue-600 text-blue-600 bg-transparent font-medium py-2 px-4 rounded-md mt-6"
                        onClick={() => navigate("/open-shifts-master")}
                    >
                        Check out open shifts
                    </button>
                </div>
            </div>
        </div>
    );
};

export default OpenShiftAvailableNotificationDetail;


